import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface SectionHandle {
  save: () => Promise<boolean>;
  discard: () => void;
}

interface InlineEditContextValue {
  /** "Edit all" mode: every editable field renders as an input. */
  globalEdit: boolean;
  setGlobalEdit: (value: boolean) => void;
  /** Pending (unsaved) changes across every registered section. */
  dirtyTotal: number;
  isSavingAll: boolean;
  saveAll: () => Promise<boolean>;
  discardAll: () => void;
  register: (id: string, handle: SectionHandle) => void;
  unregister: (id: string) => void;
  reportDirty: (id: string, count: number) => void;
}

const noop = () => undefined;

const InlineEditContext = createContext<InlineEditContextValue>({
  globalEdit: false,
  setGlobalEdit: noop,
  dirtyTotal: 0,
  isSavingAll: false,
  saveAll: async () => true,
  discardAll: noop,
  register: noop,
  unregister: noop,
  reportDirty: noop,
});

export const InlineEditProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [globalEdit, setGlobalEdit] = useState(false);
  const [dirtyCounts, setDirtyCounts] = useState<Record<string, number>>({});
  const [isSavingAll, setIsSavingAll] = useState(false);
  const handles = useRef(new Map<string, SectionHandle>());

  const register = useCallback((id: string, handle: SectionHandle) => {
    handles.current.set(id, handle);
  }, []);

  const unregister = useCallback((id: string) => {
    handles.current.delete(id);
    setDirtyCounts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const reportDirty = useCallback((id: string, count: number) => {
    setDirtyCounts((prev) =>
      prev[id] === count ? prev : { ...prev, [id]: count }
    );
  }, []);

  const dirtyTotal = useMemo(
    () => Object.values(dirtyCounts).reduce((sum, n) => sum + n, 0),
    [dirtyCounts]
  );

  // Sections are saved one after another so a failure stops the run and the
  // remaining changes stay pending (and visible) instead of half-applying.
  const saveAll = useCallback(async () => {
    setIsSavingAll(true);
    try {
      for (const [id, handle] of Array.from(handles.current.entries())) {
        if (!dirtyCounts[id]) continue;
        const ok = await handle.save();
        if (!ok) return false;
      }
      return true;
    } finally {
      setIsSavingAll(false);
    }
  }, [dirtyCounts]);

  const discardAll = useCallback(() => {
    handles.current.forEach((handle) => handle.discard());
  }, []);

  const value = useMemo(
    () => ({
      globalEdit,
      setGlobalEdit,
      dirtyTotal,
      isSavingAll,
      saveAll,
      discardAll,
      register,
      unregister,
      reportDirty,
    }),
    [
      globalEdit,
      dirtyTotal,
      isSavingAll,
      saveAll,
      discardAll,
      register,
      unregister,
      reportDirty,
    ]
  );

  return (
    <InlineEditContext.Provider value={value}>
      {children}
    </InlineEditContext.Provider>
  );
};

export const useInlineEdit = () => useContext(InlineEditContext);

/** Hooks a section into the page-level "save all / discard all". */
export const useRegisterSection = (
  id: string,
  dirtyCount: number,
  handle: SectionHandle
) => {
  const { register, unregister, reportDirty } = useInlineEdit();
  const handleRef = useRef(handle);
  handleRef.current = handle;

  useEffect(() => {
    register(id, {
      save: () => handleRef.current.save(),
      discard: () => handleRef.current.discard(),
    });
    return () => unregister(id);
  }, [id, register, unregister]);

  useEffect(() => {
    reportDirty(id, dirtyCount);
  }, [id, dirtyCount, reportDirty]);
};
